import { useLayoutEffect, useCallback, useState, MutableRefObject, Dispatch, SetStateAction, createContext, useContext } from 'react';

const defaultRectWithoutJSON = {
  bottom: 0,
  height: 0,
  left: 0,
  right: 0,
  top: 0,
  width: 0,
  x: 0,
  y: 0,
};

const defaultRect = {
  ...defaultRectWithoutJSON,
  toJSON() {
    return defaultRectWithoutJSON;
  },
} satisfies DOMRect;

function getRect(element: HTMLElement | null) {
  if (!element) {
    return defaultRect;
  }

  return element.getBoundingClientRect();
}

function returnFn(resizeObserver: { current?: ResizeObserver }) {
  return () => {
    if (!resizeObserver.current) {
      return;
    }

    resizeObserver.current.disconnect();
    resizeObserver.current = undefined;
  };
}

function handleResizeFn(ref: MutableRefObject<HTMLElement | null>, setRect: Dispatch<SetStateAction<ReturnType<typeof getRect>>>) {
  return () => {
    if (!ref.current) {
      return;
    }

    // Update client rect
    setRect(getRect(ref.current));
  };
}

function useRect(ref: MutableRefObject<HTMLElement | null>) {
  const [rect, setRect] = useState(getRect(ref.current));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(handleResizeFn(ref, setRect), [ref]);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    handleResize();

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = {
        current: new ResizeObserver(handleResize),
      };

      resizeObserver.current.observe(element);

      // eslint-disable-next-line consistent-return
      return returnFn(resizeObserver);
    }
  }, [ref, handleResize]);

  return rect;
}

/* istanbul ignore next */
const MainLayoutContext = createContext(typeof document !== 'undefined' ? document.body.getBoundingClientRect() : getRect(null));

function useMainLayout() {
  return useContext(MainLayoutContext);
}

export { useRect, useMainLayout, MainLayoutContext, returnFn as _returnFn, handleResizeFn as _handleResizeFn };
